import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Search, 
  Filter, 
  Download, 
  RefreshCw,
  Calendar,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import { ConversationDetailModal } from './ConversationDetailModal';

interface ConversationsListPageProps {
  chatbotId: string;
  onBack: () => void;
}

interface Conversation {
  id: string;
  chatbot_id: string;
  status: string;
  metadata: any;
  created_at: string;
  updated_at: string;
}

type FilterStatus = 'all' | 'completed' | 'in_progress';
type SortBy = 'created_at' | 'updated_at' | 'status';
type SortOrder = 'asc' | 'desc';

export const ConversationsListPage: React.FC<ConversationsListPageProps> = ({
  chatbotId,
  onBack
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [filteredConversations, setFilteredConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isConversationModalOpen, setIsConversationModalOpen] = useState(false);
  const { toast } = useToast();

  const fetchConversations = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('conversations')
        .select('*')
        .eq('chatbot_id', chatbotId)
        .order(sortBy, { ascending: sortOrder === 'asc' });

      if (error) throw error;
      setConversations(data || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch conversations';
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
  }, [chatbotId, sortBy, sortOrder]);

  useEffect(() => {
    let filtered = [...conversations];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(conv => {
        const metadata = conv.metadata || {};
        const searchableText = [
          conv.id,
          metadata.utm_source,
          metadata.utm_campaign,
          metadata.referrer,
          format(new Date(conv.created_at), 'MMM dd, yyyy')
        ].join(' ').toLowerCase();
        
        return searchableText.includes(searchTerm.toLowerCase());
      });
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(conv => {
        const metadata = conv.metadata || {};
        const isCompleted = !!metadata.completed_at || !!metadata.completed;
        
        if (filterStatus === 'completed') return isCompleted;
        if (filterStatus === 'in_progress') return !isCompleted;
        return true;
      });
    }

    setFilteredConversations(filtered);
  }, [conversations, searchTerm, filterStatus]);

  const handleConversationClick = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setIsConversationModalOpen(true);
  };

  const handleCloseConversationModal = () => {
    setIsConversationModalOpen(false);
    setSelectedConversationId(null);
  };

  const exportConversations = () => {
    const csvContent = [
      ['ID', 'Created At', 'Status', 'UTM Source', 'UTM Campaign', 'Response Time'],
      ...filteredConversations.map(conv => {
        const metadata = conv.metadata || {};
        const isCompleted = !!metadata.completed_at || !!metadata.completed;
        
        return [
          conv.id,
          format(new Date(conv.created_at), 'yyyy-MM-dd HH:mm:ss'),
          isCompleted ? 'Completed' : 'In Progress',
          metadata.utm_source || 'Direct',
          metadata.utm_campaign || 'N/A',
          metadata.response_time ? `${metadata.response_time.toFixed(1)}s` : 'N/A'
        ];
      })
    ]
    .map(row => row.join(','))
    .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conversations-${chatbotId}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const getStatusBadge = (conversation: Conversation) => {
    const metadata = conversation.metadata || {};
    const isCompleted = !!metadata.completed_at || !!metadata.completed;
    
    if (isCompleted) {
      return (
        <Badge variant="default" className="bg-green-100 text-green-800">
          <CheckCircle className="h-3 w-3 mr-1" />
          Completed
        </Badge>
      );
    }
    
    return (
      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
        <AlertCircle className="h-3 w-3 mr-1" />
        In Progress
      </Badge>
    );
  };

  const capitalizeString = (str: string): string => {
    return str.split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <div>
            <h1 className="text-2xl font-bold">All Conversations</h1>
            <p className="text-muted-foreground">
              {filteredConversations.length} of {conversations.length} conversations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchConversations} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportConversations}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-lg">Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search conversations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select value={filterStatus} onValueChange={(value: FilterStatus) => setFilterStatus(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Sort By</label>
              <Select value={sortBy} onValueChange={(value: SortBy) => setSortBy(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Created Date</SelectItem>
                  <SelectItem value="updated_at">Updated Date</SelectItem>
                  <SelectItem value="status">Status</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Order</label>
              <Select value={sortOrder} onValueChange={(value: SortOrder) => setSortOrder(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">Newest First</SelectItem>
                  <SelectItem value="asc">Oldest First</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversations Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Conversations
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                    <p>Loading conversations...</p>
                  </TableCell>
                </TableRow>
              ) : filteredConversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No conversations found
                  </TableCell>
                </TableRow>
              ) : (
                filteredConversations.map((conv) => {
                  const metadata = conv.metadata || {};
                  
                  return (
                    <TableRow 
                      key={conv.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleConversationClick(conv.id)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">
                              {format(new Date(conv.created_at), "MMM dd, yyyy")}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {format(new Date(conv.created_at), "HH:mm")}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {capitalizeString(metadata.utm_source || "direct")}
                      </TableCell>
                      <TableCell>
                        {metadata.utm_campaign || "N/A"}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(conv)}
                      </TableCell>
                      <TableCell>
                        {metadata.response_time
                          ? `${metadata.response_time.toFixed(1)}s`
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleConversationClick(conv.id);
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Conversation Detail Modal */}
      <ConversationDetailModal
        conversationId={selectedConversationId}
        isOpen={isConversationModalOpen}
        onClose={handleCloseConversationModal}
      />
    </div>
  );
};
